import { Injectable } from '@angular/core';
import { from, type Observable } from 'rxjs';
import type {
  AdminLocationGetDto,
  AdminLocationListDto,
  AdminLocationPostPutDto,
} from '../../../../backend/main';
import {
  createLocationAsync,
  updateLocationAsync,
  deleteAdminLocationAsync,
  findAdminLocationAsync,
  listAdminLocationsAsync,
} from '../../../../backend/main';
import type { DtoListService, GetAllRequestParameters, ListPaginatedDTO } from '@pct/ng-core-dto';
import { UNKNOWN_ITEM } from '@pct/ng-core-dto';
import { isNil } from '@pct/ts-typeguard';
import type { FilteredParams } from '../../../common/request/filtered-params';
import { mapSort } from '../../../common/request/map-sort';
import { convertPaginationParams } from '../../../common/utils/table/convert-pagination-params';
import type { LocationsFilter } from './locations-filter';

@Injectable()
export class LocationsDtoService implements DtoListService<
  AdminLocationListDto,
  FilteredParams<AdminLocationListDto, LocationsFilter>
> {
  create(data: AdminLocationPostPutDto): Observable<AdminLocationGetDto> {
    return from(createLocationAsync({ body: data }).then(resp => resp.data));
  }

  update(id: string, data: AdminLocationPostPutDto): Observable<void> {
    return from(updateLocationAsync({ path: { id }, body: data }).then(resp => resp.data));
  }

  getAll(
    param: GetAllRequestParameters<FilteredParams<AdminLocationListDto, LocationsFilter>>,
  ): Observable<ListPaginatedDTO<AdminLocationListDto>> {
    return from(
      listAdminLocationsAsync({
        body: {
          pagination: convertPaginationParams(param.pagination),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          sort: mapSort(param.parameters?.sort) as any,
          searchText: param.parameters?.filter?.searchText,
          status: param.parameters?.filter?.status,
          language: param.parameters?.filter?.language,
          categoryIds: param.parameters?.filter?.categoryIds ?? [],
        },
      }).then(resp => resp.data),
    );
  }

  getById(id: string): Observable<AdminLocationGetDto> {
    return from(findAdminLocationAsync({ path: { id } }).then(resp => resp.data));
  }

  getListItemById(id: string): Observable<Partial<AdminLocationListDto>> {
    return from(
      findAdminLocationAsync({ path: { id } })
        .then(resp => resp.data)
        .then(data => {
          const translation =
            data.translations.find(t => t.language === 'hu') ??
            data.translations.find(t => t.language === 'en');
          return {
            id: data.id,
            title: translation?.title ?? 'N/A',
          };
        }),
    );
  }

  getDtoId(dto: AdminLocationListDto): string;
  getDtoId(dto: AdminLocationListDto | null | undefined): string | null | undefined;
  getDtoId(dto: Partial<AdminLocationListDto> | null | undefined): string | null | undefined;
  getDtoId(
    dto: AdminLocationListDto | Partial<AdminLocationListDto> | null | undefined,
  ): string | null | undefined {
    if (isNil(dto)) {
      return dto;
    }
    return dto.id;
  }

  toString(dto: AdminLocationListDto): string {
    return dto.title ?? UNKNOWN_ITEM;
  }

  delete(id: string): Observable<void> {
    return from(
      deleteAdminLocationAsync({ path: { id } }).then(() => {
        // type cast to void
      }),
    );
  }
}
